import type { ChildAllowanceConfig, FederalStateConfig } from '@/ts/types/config.types'
import { BmfApiService } from '@/services/api/bmf.service.ts'
import { assertPrimitive } from '@/helpers/assert.helpers.ts'
import { getBasicAuth } from './basic-auth.helpers'

export function calculateMonetaryBenefit(uvp: number) {
  assertPrimitive(uvp, 'number')

  const factor = 0.01
  const uvpFraction = uvp / 4
  const roundedUvpFraction = Math.trunc(uvpFraction / 100) * 100 // round to down to full hundredth
  return roundedUvpFraction * factor
}

export function calculateChurchTax(
  isLiableToChurchTax: boolean,
  calculationBasis: number,
  federalState: FederalStateConfig | undefined
): number {
  assertPrimitive(isLiableToChurchTax, 'boolean')
  assertPrimitive(calculationBasis, 'number')

  const churchTaxMultiplier = isLiableToChurchTax ? (federalState?.church_tax ?? 0) : 0
  return calculationBasis * churchTaxMultiplier
}

export function calculateChildAllowanceSavings(childAllowanceValue: number, subsistenceLevel: number, careNeeds: number): number {
  assertPrimitive(childAllowanceValue, 'number')
  assertPrimitive(subsistenceLevel, 'number')
  assertPrimitive(careNeeds, 'number')
  return (subsistenceLevel + careNeeds) * childAllowanceValue
}

export async function getBmfTaxes(
  calculationBasis: number,
  childAllowanceValue: number,
  childAllowanceConfig: ChildAllowanceConfig,
  taxClass: number,
  isLiableToChurchTax: boolean,
  isLiableToPensionInsurance: boolean,
  isLiableToHealthInsurance: boolean,
  federalState: string,
  employeeAdditionalSocialInsurance: number
): Promise<{ salaryTax: number; solidarityTax: number; churchTaxCalculationBasis: number }> {
  assertPrimitive(calculationBasis, 'number')
  assertPrimitive(childAllowanceValue, 'number')
  assertPrimitive(taxClass, 'number')
  assertPrimitive(isLiableToChurchTax, 'boolean')
  assertPrimitive(isLiableToPensionInsurance, 'boolean')
  assertPrimitive(isLiableToHealthInsurance, 'boolean')
  assertPrimitive(federalState, 'string')
  assertPrimitive(employeeAdditionalSocialInsurance, 'number')

  const basicAuth = getBasicAuth()

  const bmfApiService = new BmfApiService(basicAuth)
  const yearlyGross = calculationBasis * 12
  const childAllowanceSavings = childAllowanceValue
    ? calculateChildAllowanceSavings(childAllowanceValue, childAllowanceConfig.subsistence_level, childAllowanceConfig.care_needs)
    : 0
  // transform calculation basis for tax api to cents and cut away remaining decimals
  const newCalculationBasisInCents = parseInt(`${yearlyGross * 100}`)

  const easternGermanyFederalStates = new Set(['Sachsen', 'Brandenburg', 'Sachsen-Anhalt', 'Thüringen', 'Mecklemburg-Vorpommern'])
  const isInEasternGermany = easternGermanyFederalStates.has(federalState)
  const childCount = childAllowanceValue * 2
  const consideredChildAllowanceAboveOne = childCount >= 1 ? (childCount > 4 ? 4 : childCount - 1) : 0

  const { solidarityTax, salaryTax, churchTaxCalculationBasis } = await bmfApiService.getPayrollTax(
    newCalculationBasisInCents,
    taxClass,
    isLiableToChurchTax ? 1 : 0,
    isLiableToPensionInsurance ? 0 : 1,
    childCount ? 0 : 1,
    isLiableToHealthInsurance ? 0 : 1,
    consideredChildAllowanceAboveOne,
    taxClass <= 4 ? childAllowanceValue : 0,
    employeeAdditionalSocialInsurance,
    federalState === 'Sachsen' ? 1 : 0
  )
  return {
    salaryTax: salaryTax / 12 / 100,
    solidarityTax: solidarityTax / 12 / 100,
    churchTaxCalculationBasis: churchTaxCalculationBasis / 12 / 100
  }
}

export async function calculateTaxes(
  calculationBasis: number,
  federalState: FederalStateConfig | undefined,
  isLiableToChurchTax: boolean,
  childAllowanceValue: number,
  childAllowanceConfig: ChildAllowanceConfig,
  taxClass: number,
  isLiableToStatutoryPensionTax: boolean,
  isLiableToStatutoryCareInsurance: boolean,
  isLiableToStatutoryHealthInsurance: boolean,
  healthInsuranceAdditionalContribution: number
) {
  assertPrimitive(calculationBasis, 'number')
  assertPrimitive(isLiableToChurchTax, 'boolean')
  assertPrimitive(childAllowanceValue, 'number')
  assertPrimitive(taxClass, 'number')
  assertPrimitive(isLiableToStatutoryPensionTax, 'boolean')
  assertPrimitive(isLiableToStatutoryCareInsurance, 'boolean')
  assertPrimitive(isLiableToStatutoryHealthInsurance, 'boolean')
  assertPrimitive(healthInsuranceAdditionalContribution, 'number')
  const { salaryTax, solidarityTax, churchTaxCalculationBasis } = await getBmfTaxes(
    calculationBasis,
    childAllowanceValue,
    childAllowanceConfig,
    taxClass,
    isLiableToChurchTax,
    isLiableToStatutoryPensionTax,
    isLiableToStatutoryHealthInsurance,
    federalState!.name,
    healthInsuranceAdditionalContribution
  )
  const churchTax = calculateChurchTax(isLiableToChurchTax, churchTaxCalculationBasis, federalState)
  return salaryTax + solidarityTax + churchTax
}

export function calculateLeasingRate(uvp: number, leasingFactor: number, employerTaxDeduction: boolean, tax: number = 0.19): number {
  assertPrimitive(uvp, 'number')
  assertPrimitive(leasingFactor, 'number')
  assertPrimitive(employerTaxDeduction, 'boolean')
  assertPrimitive(tax, 'number')
  const leasingRateCalculationBasis = employerTaxDeduction ? uvp / (tax + 1) : uvp
  return leasingRateCalculationBasis * leasingFactor
}

export function calculateLeasingCalculationBasis(
  monthlySalary: number,
  employerSubsidy: number,
  assumptionOfInsuranceCosts: boolean,
  leasingRate: number,
  insuranceBonus: number,
  monetaryAdvantage: number
): number {
  assertPrimitive(monthlySalary, 'number')
  assertPrimitive(employerSubsidy, 'number')
  assertPrimitive(leasingRate, 'number')
  assertPrimitive(insuranceBonus, 'number')
  assertPrimitive(monetaryAdvantage, 'number')
  assertPrimitive(assumptionOfInsuranceCosts, 'boolean')

  const newInsuranceBonus = !assumptionOfInsuranceCosts ? insuranceBonus : 0

  return monthlySalary + employerSubsidy - leasingRate - newInsuranceBonus + monetaryAdvantage
}

export function calculateExpectedUsedPurchasePrice(uvp: number, usedPurchaseFactor: number) {
  assertPrimitive(uvp, 'number')
  assertPrimitive(usedPurchaseFactor, 'number')

  return uvp * usedPurchaseFactor
}

export function calculateInsuranceCosts(insuranceBonus: number, duration: number): number {
  assertPrimitive(insuranceBonus, 'number')
  assertPrimitive(duration, 'number')

  return insuranceBonus * duration
}

export function calculateLeasingNetChargeOverDuration(leasingMonthlyNetBurden: number, duration: number): number {
  assertPrimitive(leasingMonthlyNetBurden, 'number')
  assertPrimitive(duration, 'number')

  return leasingMonthlyNetBurden * duration
}

export function calculateTotalCostsLeasing(netBurdenOverDuration: number, expectedUsedPurchasePrice: number): number {
  assertPrimitive(netBurdenOverDuration, 'number')
  assertPrimitive(expectedUsedPurchasePrice, 'number')

  return netBurdenOverDuration + expectedUsedPurchasePrice
}

export function calculateTotalCostPrivateBuy(purchasePrice: number, insuranceCosts: number): number {
  assertPrimitive(purchasePrice, 'number')
  assertPrimitive(insuranceCosts, 'number')

  return purchasePrice + insuranceCosts
}

export function calculateAbsoluteSavings(totalCostsPrivateBuy: number, totalCostsLeasing: number): number {
  assertPrimitive(totalCostsPrivateBuy, 'number')
  assertPrimitive(totalCostsLeasing, 'number')

  return totalCostsPrivateBuy - totalCostsLeasing
}

export function calculateRelativeSavings(absoluteSavings: number, totalCostsPrivateBuy: number): number {
  assertPrimitive(absoluteSavings, 'number')
  assertPrimitive(totalCostsPrivateBuy, 'number')

  return absoluteSavings / totalCostsPrivateBuy
}

export function calculateNetPayout(calculationBasis: number, tax: number, socialInsurance: number, monetaryAdvantage: number = 0) {
  assertPrimitive(calculationBasis, 'number')
  assertPrimitive(tax, 'number')
  assertPrimitive(socialInsurance, 'number')
  assertPrimitive(monetaryAdvantage, 'number')

  return calculationBasis - tax - socialInsurance - monetaryAdvantage
}

export function calculateMonthlyNetBurden(privateBuyNetPayout: number, leasingNetPayout: number) {
  assertPrimitive(privateBuyNetPayout, 'number')
  assertPrimitive(leasingNetPayout, 'number')

  return privateBuyNetPayout - leasingNetPayout
}

export function calculateEmployeeContribution(employeeValue: number, withoutChildrenBonus: number): number {
  return employeeValue + withoutChildrenBonus
}

export function calculateCareInsurance(
  calculationBasis: number,
  employeeValue: number,
  withoutChildrenBonus: number,
  contributionAssessmentLimit: number,
  isLiableToHealthInsurance: boolean
): number {
  assertPrimitive(calculationBasis, 'number')
  assertPrimitive(employeeValue, 'number')
  assertPrimitive(withoutChildrenBonus, 'number')
  assertPrimitive(contributionAssessmentLimit, 'number')
  assertPrimitive(isLiableToHealthInsurance, 'boolean')

  if (!isLiableToHealthInsurance) return 0

  const innerCalculationBasis =
    calculationBasis * 12 >= contributionAssessmentLimit ? calculateMonthlyValue(contributionAssessmentLimit) : calculationBasis

  return innerCalculationBasis * calculateEmployeeContribution(employeeValue, withoutChildrenBonus)
}

export function calculateUnemploymentInsurance(
  monthlyGross: number,
  contributionAssessmentLimit: number,
  employeeValue: number,
  isLiableToPensionInsurance: boolean
): number {
  assertPrimitive(monthlyGross, 'number')
  assertPrimitive(contributionAssessmentLimit, 'number')
  assertPrimitive(employeeValue, 'number')
  assertPrimitive(isLiableToPensionInsurance, 'boolean')
  if (!isLiableToPensionInsurance) return 0

  if (monthlyGross * 12 >= contributionAssessmentLimit) return (contributionAssessmentLimit / 12) * employeeValue
  return monthlyGross * employeeValue
}

export function calculateHealthInsuranceFactor(
  healthInsuranceEmployeeValue: number,
  healthInsuranceAdditionalContribution: number
): number {
  assertPrimitive(healthInsuranceEmployeeValue, 'number')
  assertPrimitive(healthInsuranceAdditionalContribution, 'number')
  return healthInsuranceEmployeeValue + healthInsuranceAdditionalContribution / (2 * 100)
}

export function calculateHealthInsurance(
  calculationBasis: number,
  healthInsuranceEmployeeValue: number,
  healthInsuranceAdditionalContribution: number,
  healthInsuranceContributionAssessmentLimit: number,
  isPrivate: boolean
): number {
  assertPrimitive(calculationBasis, 'number')
  assertPrimitive(healthInsuranceEmployeeValue, 'number')
  assertPrimitive(healthInsuranceAdditionalContribution, 'number')
  assertPrimitive(healthInsuranceContributionAssessmentLimit, 'number')
  assertPrimitive(isPrivate, 'boolean')
  if (isPrivate) return 0

  const healthInsuranceFactor = calculateHealthInsuranceFactor(healthInsuranceEmployeeValue, healthInsuranceAdditionalContribution)

  if (calculationBasis * 12 >= healthInsuranceContributionAssessmentLimit)
    return calculateMonthlyValue(healthInsuranceContributionAssessmentLimit) * healthInsuranceFactor
  return calculationBasis * healthInsuranceFactor
}

export function calculatePensionInsurance(
  monthlyGross: number,
  pensionInsuranceContributionAssessmentLimit: number,
  employeeValue: number,
  isLiableToPensionInsurance: boolean
): number {
  assertPrimitive(monthlyGross, 'number')
  assertPrimitive(pensionInsuranceContributionAssessmentLimit, 'number')
  assertPrimitive(employeeValue, 'number')
  assertPrimitive(isLiableToPensionInsurance, 'boolean')
  if (!isLiableToPensionInsurance) return 0

  if (monthlyGross * 12 >= pensionInsuranceContributionAssessmentLimit)
    return (pensionInsuranceContributionAssessmentLimit / 12) * employeeValue
  return monthlyGross * employeeValue
}

export function calculateSocialInsuranceContribution(
  pensionInsurance: number,
  unemploymentInsurance: number,
  healthInsurance: number,
  careInsurance: number
): number {
  assertPrimitive(pensionInsurance, 'number')
  assertPrimitive(unemploymentInsurance, 'number')
  assertPrimitive(healthInsurance, 'number')
  assertPrimitive(careInsurance, 'number')
  return pensionInsurance + unemploymentInsurance + healthInsurance + careInsurance
}

export function calculateMonthlyValue(value: number): number {
  return value / 12
}
